import type { ComputedRef } from "vue";
import type { FormItems } from "@/composables/forms/formTypes";
import { ref, watch, onMounted } from "vue";
import { Override_Options_Enum } from "gql/api";
import {
  getFieldName,
  getFieldValue,
  updateGasSeparationFields,
  type Stack
} from "@/composables/forms/stackAwareFields";
import { valuesEqual } from "./useGasSeparationOverride";

type GasSeparationType = "naturalGasSeparation" | "intakeGasSeparation";

interface GasSeparationConfig {
  overrideTypeFieldKey:
    | "naturalGasSepOverrideType"
    | "intakeGasSepOverrideType";
  percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent";
  calculatedValue: ComputedRef<number>;
}

/**
 * Composable for managing packer state and gas separation override restoration
 */
export function usePackerWatcher(
  form: FormItems["parameters"] | FormItems["performanceEvaluation"],
  packerInstalled: ComputedRef<boolean>,
  gasSeparations: Partial<Record<GasSeparationType, GasSeparationConfig>>,
  stack?: Stack
) {
  // Store the state before packer is turned on, so we can restore it when packer is turned off
  const storedOverridePercents = ref<
    Partial<Record<GasSeparationType, number | null | undefined>>
  >({});

  // Track if component is mounted to avoid acting on initial mount
  const isMounted = ref(false);
  onMounted(() => {
    isMounted.value = true;
  });

  /**
   * Helper function to restore override state based on percent value
   */
  function restoreOverrideState(
    percent: number | null | undefined,
    calculatedValue: number,
    overrideTypeFieldKey:
      | "naturalGasSepOverrideType"
      | "intakeGasSepOverrideType",
    percentFieldKey: "naturalGasSepPercent" | "intakeGasSepPercent"
  ) {
    const overrideTypeFieldName = getFieldName(overrideTypeFieldKey, stack);
    const percentFieldName = getFieldName(percentFieldKey, stack);

    if (
      percent !== null &&
      percent !== undefined &&
      !valuesEqual(percent, calculatedValue)
    ) {
      // Percent differs from calculated - restore to Override
      updateGasSeparationFields(form, {
        [overrideTypeFieldName]: Override_Options_Enum.Override,
        [percentFieldName]: percent
      });
    } else {
      // Percent matches calculated or is null - restore to Calculate
      updateGasSeparationFields(form, {
        [overrideTypeFieldName]: Override_Options_Enum.Calculate,
        [percentFieldName]: null
      });
    }
  }

  watch(packerInstalled, (packerInstalledValue, previousValue) => {
    // Only act on actual state changes (not initial mount)
    if (!isMounted.value) return;

    if (packerInstalledValue && !previousValue) {
      // Packer was just turned ON - store current state before clearing
      for (const [type, config] of Object.entries(gasSeparations)) {
        if (!config) continue;
        const currentOverrideType = getFieldValue(
          form.data.value,
          config.overrideTypeFieldKey,
          stack
        ) as Override_Options_Enum | null | undefined;
        const currentOverridePercent = getFieldValue(
          form.data.value,
          config.percentFieldKey,
          stack
        ) as number | null | undefined;

        // Store percent values if we're in override mode
        if (currentOverrideType === Override_Options_Enum.Override) {
          storedOverridePercents.value[type as GasSeparationType] =
            currentOverridePercent;
        }

        // Set gas separation override type to Disable
        const overrideTypeFieldName = getFieldName(
          config.overrideTypeFieldKey,
          stack
        );
        const percentFieldName = getFieldName(config.percentFieldKey, stack);
        updateGasSeparationFields(form, {
          [overrideTypeFieldName]: Override_Options_Enum.Disable,
          [percentFieldName]: null
        });
      }
    } else if (!packerInstalledValue && previousValue) {
      // Packer was just turned OFF - restore stored state based on percent values
      for (const [type, config] of Object.entries(gasSeparations)) {
        if (!config) continue;
        const calculatedValue = config.calculatedValue.value;
        const storedPercent =
          storedOverridePercents.value[type as GasSeparationType];

        if (storedPercent !== null && storedPercent !== undefined) {
          // Restore from stored value
          restoreOverrideState(
            storedPercent,
            calculatedValue,
            config.overrideTypeFieldKey,
            config.percentFieldKey
          );
          storedOverridePercents.value[type as GasSeparationType] = undefined;
        } else {
          // No stored value - check initialData or default to Calculate
          const initialPercent = form.initialData.value
            ? (getFieldValue(
                form.initialData.value,
                config.percentFieldKey,
                stack
              ) as number | null | undefined)
            : undefined;

          restoreOverrideState(
            initialPercent,
            calculatedValue,
            config.overrideTypeFieldKey,
            config.percentFieldKey
          );
        }
      }
    }
  });

  return {
    storedOverridePercents
  };
}
